// Utilities for date parsing/formatting in local time, avoiding UTC off-by-one

/**
 * Parse a value into a Date using local timezone semantics.
 * - If value is a plain YYYY-MM-DD string, construct Date(y, m-1, d) locally.
 * - Otherwise, fallback to new Date(value).
 */
export function parseLocalDate(value: string | number | Date): Date {
  if (value == null) return new Date(NaN);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map((n) => parseInt(n, 10));
    return new Date(y, (m || 1) - 1, d || 1);
  }
  return new Date(value as any);
}

/**
 * Format a date-like value to a locale date string using local timezone semantics.
 * Returns '-' if the value cannot be parsed to a valid date.
 */
export function formatLocalDate(
  value: string | number | Date,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = parseLocalDate(value);
  if (isNaN(d.getTime())) return '-';
  return locale || options ? d.toLocaleDateString(locale, options) : d.toLocaleDateString();
}

/**
 * Timezone offset mapping (hours from UTC)
 * Handles both standard and daylight saving time
 */
const TIMEZONE_OFFSETS: Record<string, { standard: number; dst: number }> = {
  alaska: { standard: -9, dst: -8 }, // AKST/AKDT
  central: { standard: -6, dst: -5 }, // CST/CDT
  eastern: { standard: -5, dst: -4 }, // EST/EDT
  hawaii: { standard: -10, dst: -10 }, // HST (no DST)
  mountain: { standard: -7, dst: -6 }, // MST/MDT
  pacific: { standard: -8, dst: -7 }, // PST/PDT
};

/**
 * Check if a date is in daylight saving time for a given timezone
 * DST typically runs from 2nd Sunday in March to 1st Sunday in November
 */
function isDST(date: Date, timezone: string): boolean {
  // Hawaii doesn't observe DST
  if (timezone === 'hawaii') return false;

  const year = date.getFullYear();

  // DST starts: 2nd Sunday in March at 2 AM
  const marchFirst = new Date(year, 2, 1); // March 1
  const dstStart = new Date(year, 2, ((14 - marchFirst.getDay()) % 7) + 8, 2, 0, 0);

  // DST ends: 1st Sunday in November at 2 AM
  const novFirst = new Date(year, 10, 1); // November 1
  const dstEnd = new Date(year, 10, ((7 - novFirst.getDay()) % 7) + 1, 2, 0, 0);

  return date >= dstStart && date < dstEnd;
}

/**
 * Get timezone offset for a specific timezone and date
 */
function getTimezoneOffset(timezone: string, date: Date): number {
  const tz = TIMEZONE_OFFSETS[timezone.toLowerCase()] || TIMEZONE_OFFSETS.eastern;
  return isDST(date, timezone.toLowerCase()) ? tz.dst : tz.standard;
}

/**
 * Parse game time string to hours and minutes
 * Handles both "7:00 PM" and "19:00" formats
 */
function parseGameTime(timeStr: string): { hours: number; minutes: number } {
  const trimmed = timeStr.trim();
  let hours = 0;
  let minutes = 0;

  // Handle "7:00 PM" format
  if (trimmed.includes('AM') || trimmed.includes('PM')) {
    const isPM = trimmed.toUpperCase().includes('PM');
    const timeWithoutAMPM = trimmed.replace(/\s*(AM|PM)/i, '').trim();
    const [hoursStr, minutesStr] = timeWithoutAMPM.split(':');
    hours = parseInt(hoursStr, 10);
    minutes = parseInt(minutesStr, 10) || 0;

    // Convert to 24-hour format
    if (isPM && hours !== 12) {
      hours += 12;
    } else if (!isPM && hours === 12) {
      hours = 0;
    }
  } else {
    // Handle "19:00" format
    const [hoursStr, minutesStr] = trimmed.split(':');
    hours = parseInt(hoursStr, 10);
    minutes = parseInt(minutesStr, 10) || 0;
  }

  return { hours, minutes };
}

/**
 * Calculate hours since game started (timezone-aware)
 * Returns positive number if game is in the past, negative if in the future
 *
 * @param game - Game object with date, time, and timezone fields
 * @param game.date - Game date in YYYY-MM-DD format
 * @param game.time - Game time (optional, e.g., "7:00 PM" or "19:00")
 * @param game.timezone - Game timezone (optional, defaults to 'eastern')
 * @returns Hours since game started (negative if in future, -999 on error)
 */
export function getHoursSinceGameStart(game: { date: string; time?: string; timezone?: string }): number {
  try {
    if (!game || !game.date) {
      return -999; // Return large negative to indicate invalid/missing data
    }

    // Parse game date
    const [year, month, day] = game.date.split('-').map((num) => parseInt(num, 10));

    // Parse game time or default to midnight
    const { hours, minutes } = game.time ? parseGameTime(game.time) : { hours: 0, minutes: 0 };

    // Get timezone offset for the game
    const timezone = game.timezone || 'eastern';

    // Create a date in the game's timezone
    // We create it in local time then adjust for timezone difference
    const gameLocalDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

    // Get current time
    const now = new Date();

    // Get timezone offsets (in hours)
    const gameTimezoneOffset = getTimezoneOffset(timezone, gameLocalDate);
    const localTimezoneOffset = -now.getTimezoneOffset() / 60; // Convert minutes to hours, flip sign

    // Calculate the difference in timezone offsets
    const timezoneDiffHours = localTimezoneOffset - gameTimezoneOffset;

    // Calculate hours difference
    const millisecondsDiff = now.getTime() - gameLocalDate.getTime();
    const hoursDiff = millisecondsDiff / (1000 * 60 * 60);

    // Adjust for timezone difference
    const adjustedHoursDiff = hoursDiff + timezoneDiffHours;

    return adjustedHoursDiff;
  } catch (error) {
    console.error('Error calculating hours since game start:', error);
    return -999; // Return large negative on error to be safe (treat as not locked)
  }
}

/**
 * Get absolute timestamp for a game (timezone-aware)
 * Returns the timestamp in milliseconds since epoch
 *
 * @param game - Game object with date, time, and timezone fields
 * @param game.date - Game date in YYYY-MM-DD format
 * @param game.time - Game time (optional, e.g., "7:00 PM" or "19:00")
 * @param game.timezone - Game timezone (optional, defaults to 'eastern')
 * @returns Timestamp in milliseconds (0 on error)
 */
export function getGameTimestamp(game: { date: string; time?: string; timezone?: string }): number {
  try {
    if (!game || !game.date) {
      return 0;
    }

    // Parse game date
    const [year, month, day] = game.date.split('-').map((num) => parseInt(num, 10));

    // Parse game time or default to midnight
    const { hours, minutes } = game.time ? parseGameTime(game.time) : { hours: 0, minutes: 0 };

    // Get timezone offset for the game
    const timezone = game.timezone || 'eastern';

    // Create a date in the game's timezone
    const gameLocalDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

    // Get timezone offsets (in hours)
    const gameTimezoneOffset = getTimezoneOffset(timezone, gameLocalDate);
    const localTimezoneOffset = -new Date().getTimezoneOffset() / 60;

    // Calculate the difference in timezone offsets (in milliseconds)
    const timezoneDiffMs = (localTimezoneOffset - gameTimezoneOffset) * 60 * 60 * 1000;

    // Return adjusted timestamp
    return gameLocalDate.getTime() - timezoneDiffMs;
  } catch (error) {
    console.error('Error calculating game timestamp:', error);
    return 0;
  }
}
